import type { ApolloClient, ObservableQuery } from '@apollo/client'
import { useApolloClient } from '@apollo/client/react'
import { getMainDefinition } from '@apollo/client/utilities'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { logError } from '~publish/legacy/utils/logError'
import { useBufferRealTimeSub } from '~publish/services/realtime'

import { ContentItemNotesSummaryQuery } from './contentItemNotesSummary'
import {
  fetchCreatedNote,
  insertCreatedNote,
  removeDeletedNote,
  replaceUpdatedNote,
} from './notesCache'
import { CollaborationNoteWithActionsFields, NotesQuery } from './notesQueries'
import { contentItemNoteSubject } from './noteSubject'

export const ContentItemNoteChanges = graphql(
  /* GraphQL */ `
    subscription ContentItemNoteChanges($contentItemId: ContentItemId!) {
      collaborationNoteSubjectChanges(
        input: { subject: { contentItemId: $contentItemId } }
      ) {
        __typename
        ... on CollaborationNoteSubjectNoteChanged {
          kind
          note {
            ...CollaborationNoteWithActionsFields
          }
        }
        ... on CollaborationNoteSubjectNoteDeleted {
          noteId
        }
      }
    }
  `,
  [CollaborationNoteWithActionsFields],
)

type ContentItemNoteChange = ResultOf<
  typeof ContentItemNoteChanges
>['collaborationNoteSubjectChanges']

const NOTES_FEED_QUERY_NAME = getMainDefinition(NotesQuery).name?.value
const SUMMARY_QUERY_NAME = getMainDefinition(ContentItemNotesSummaryQuery).name
  ?.value

/**
 * The queries one ContentItem thread reads: its feed and its thread state.
 * The stream replays nothing, so both refetch after each connect.
 */
const readsThread =
  (contentItemId: string) =>
  (query: ObservableQuery): boolean => {
    const variables: Record<string, unknown> = query.variables ?? {}
    if (query.queryName === SUMMARY_QUERY_NAME) {
      return variables.contentItemId === contentItemId
    }
    if (query.queryName !== NOTES_FEED_QUERY_NAME) return false
    const input = variables.input as
      | { subject?: { contentItemId?: string } | null }
      | undefined
    return input?.subject?.contentItemId === contentItemId
  }

async function applyChange(
  client: ApolloClient,
  contentItemId: string,
  change: ContentItemNoteChange,
): Promise<void> {
  const subject = contentItemNoteSubject(contentItemId)
  if (change.__typename === 'CollaborationNoteSubjectNoteDeleted') {
    removeDeletedNote(client.cache, subject, change.noteId)
    return
  }
  if (change.kind === 'updated') {
    replaceUpdatedNote(client.cache, subject, change.note)
    return
  }
  // The shared event carries no cursor and no allowed actions for this
  // reader, and the feed pages from the cursors it holds, so another
  // account's new Note is read back from the first page with its edge. The
  // account's own creates are inserted from the create response instead; if
  // this event arrives for one, the read back replaces the same edge.
  const edge = await fetchCreatedNote(client, subject, change.note.id)
  if (edge) insertCreatedNote(client.cache, subject, edge)
}

/**
 * Keeps one ContentItem's Notes thread live while it is open, through
 * `collaborationNoteSubjectChanges`. Each open thread holds its own stream.
 */
export function useContentItemNoteChanges(contentItemId: string): void {
  const client = useApolloClient()

  useBufferRealTimeSub(ContentItemNoteChanges, {
    variables: { contentItemId },
    refetchOnReconnect: readsThread(contentItemId),
    onData: (data): void => {
      applyChange(
        client,
        contentItemId,
        data.collaborationNoteSubjectChanges,
      ).catch(logError)
    },
    onError: logError,
  })
}
